import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import {
  setSearch,
  addAchievements,
  setActiveAchievement,
  setShowAchievementDetails,
} from './Achievements.slice';
import { useIntl } from 'react-intl';
import messages from './messages';
import debounce from 'lodash/debounce';
import AchievementsService from '../../service/Achievements.service';
import {
  deleteAchievement,
  fetchAchievements,
  updateAchievement,
} from './Achievements.actions';
import { Achievement, AchievementStatus } from './achievements';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorPrim,
  fontColorSecond,
  orange,
  red,
  white,
} from '../../assets/styledAssets/vars';
import Button from '../../components/Button/Button.component';
import {
  DeleteIcon,
  PlusIcon,
  SearchIcon,
} from '../../components/Icons/Icons.component';
import FormField from '../../components/FormField/FormField.component';
import { GridColumn } from '@progress/kendo-react-grid';
import Moment from 'react-moment';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import AchievementDetails from './components/AchievementDetails/AchievementDetails.component';
import { Gender } from '../../domains/app';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';
import { WorldRank, WorldRecordType } from '../WorldRecords/worldrecords';
import { convertToTimeMask } from '../../helpers/app.helpers';
import { WorldRankValue } from '../WorldRecords/constants';

const connector = connect(
  (state: RootState) => ({
    listAchievements: state.achievements.listAchievements,
    search: state.achievements.search,
    isFetching: state.achievements.isFetching,
    total: state.achievements.total,
    pageSize: state.achievements.pageSize,
    user: state.app.user,
    activeAchievement: state.achievements.activeAchievement,
    showAchievementDetails: state.achievements.showAchievementDetails,
  }),
  {
    setSearch,
    addAchievements,
    setActiveAchievement,
    fetchAchievements,
    updateAchievement,
    setShowAchievementDetails,
    deleteAchievement,
  },
);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Achievements: FunctionComponent<PropsFromRedux> = ({
  listAchievements,
  search,
  isFetching,
  total,
  pageSize,
  user,
  activeAchievement,
  setSearch,
  addAchievements,
  setActiveAchievement,
  fetchAchievements,
  updateAchievement,
  setShowAchievementDetails,
  showAchievementDetails,
  deleteAchievement,
}) => {
  const intl = useIntl();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationHandler, setConfirmationHandler] = useState<() => void>();
  const [skip, setSkip] = useState<number>(0);
  const [statusMap] = useState<Record<AchievementStatus, string>>({
    [AchievementStatus.Pending]: intl.formatMessage(messages.pending),
    [AchievementStatus.Confirmed]: intl.formatMessage(messages.confirmed),
    [AchievementStatus.Declined]: intl.formatMessage(messages.declined),
  });

  useEffect(() => {
    fetchAchievements(true);
    // eslint-disable-next-line
  }, []);

  const fetch = useMemo(
    () =>
      debounce(() => {
        fetchAchievements(true);
      }, 200),
    // eslint-disable-next-line
    [],
  );

  const handleChangeInput = (e: any) => {
    setSearch(e.target.value);
    fetch();
  };

  const handleAddEditAchievement = useCallback(
    (a: Achievement) => {
      if (a.id) {
        updateAchievement(a);
      } else {
        a.personId = user?.id as string;
        a.male = user?.gender === Gender.Male;
        AchievementsService.createAchievement(a).then((res) => {
          addAchievements([res]);
          setShowAchievementDetails(false);
        });
      }
    },
    // eslint-disable-next-line
    [],
  );

  const removeRecord = (id: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.deleteAchievement));
    setConfirmationHandler(() => {
      return () => {
        deleteAchievement(id);
      };
    });
  };

  const pageHandler = useCallback(
    (event: any) => {
      setSkip(event.page.skip);
      if (
        skip + pageSize + 3 >= listAchievements.length &&
        !isFetching &&
        total > listAchievements.length
      ) {
        fetchAchievements(false);
      }
    },
    [
      isFetching,
      listAchievements.length,
      total,
      fetchAchievements,
      setSkip,
      skip,
      pageSize,
    ],
  );

  return (
    <Wrap
      flexDir={'column'}
      flexWrap={'nowrap'}
      overflow={['auto', 'unset']}
      width={'100%'}
    >
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap
          alignItems={'center'}
          width={'100%'}
          justifyCont={'space-between'}
        >
          <Wrap
            alignItems={['flex-start', 'flex-end']}
            flexDir={['column', 'row']}
          >
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[
                [{ side: 'right', color: 'transparent' }],
                [{ side: 'right', color: accentColorSecond }],
              ]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 1rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.title)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {total} {intl.formatMessage(messages.total)}
            </Wrap>
          </Wrap>
          <Wrap justifyCont={'flex-end'}>
            <Button primary onClick={() => setShowAchievementDetails(true)}>
              <PlusIcon color={white} width={'1.4rem'} />
              {intl.formatMessage(messages.addAchievement)}
            </Button>
          </Wrap>
        </Wrap>
        <Wrap
          alignItems={'center'}
          margin={['2rem 0', '4rem 0 3rem 0']}
          maxWidth={'34rem'}
          width={'100%'}
        >
          <FormField
            placeholder={'Search'}
            value={search}
            onChange={handleChangeInput}
            postfix={<SearchIcon color={fontColorPrim} width={'2rem'} />}
          />
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable
          style={{ height: '100%', width: '100%' }}
          data={listAchievements.slice(skip, skip + pageSize)}
          onRowClick={(e) => {
            setActiveAchievement(e.dataItem);
            setShowAchievementDetails(true);
          }}
          onPageChange={pageHandler}
          scrollable={'virtual'}
          skip={skip}
          pageSize={pageSize}
          total={total}
          rowHeight={55}
        >
          <GridColumn
            field="event.name"
            title={intl.formatMessage(messages.event)}
            width={'150px'}
          />
          <GridColumn
            field="levelOrResult"
            title={intl.formatMessage(messages.result)}
            width={'150px'}
            cell={(props) => (
              <td>
                {props.dataItem.event.input === WorldRecordType.Time
                  ? convertToTimeMask(props.dataItem.levelOrResult)
                  : props.dataItem.event.input === WorldRecordType.Rank
                  ? WorldRankValue[props.dataItem.levelOrResult as WorldRank]
                  : props.dataItem.levelOrResult}
              </td>
            )}
          />
          <GridColumn
            field="rankPercentage"
            title={intl.formatMessage(messages.percentage)}
            width={'150px'}
          />
          <GridColumn
            field="achievedOrStart"
            title={intl.formatMessage(messages.startDate)}
            width={'150px'}
            cell={(props) => (
              <td>
                <Moment format={'DD MMM YYYY'}>
                  {props.dataItem.achievedOrStart}
                </Moment>
              </td>
            )}
          />
          {/*          <GridColumn
            field="startDate"
            title={intl.formatMessage(messages.end)}
            width={'150px'}
            cell={(props) => (
              <td>
                <Moment format={'DD MMM YYYY'}>{props.dataItem.endDate}</Moment>
              </td>
            )}
          />*/}
          <GridColumn
            field="confirmedByPerson.firstName"
            title={intl.formatMessage(messages.confirmedBy)}
            cell={(props) => (
              <td>
                {props.dataItem.status !== AchievementStatus.Pending
                  ? `${props.dataItem.confirmedByPerson?.firstName} ${props.dataItem.confirmedByPerson?.lastName}`
                  : '-'}
              </td>
            )}
          />
          <GridColumn
            field="confirmedByPerson.email"
            title={intl.formatMessage(messages.email)}
            width={'200px'}
            cell={(props) => (
              <td>
                {props.dataItem.status !== AchievementStatus.Pending
                  ? `${props.dataItem.confirmedByPerson?.email}`
                  : '-'}
              </td>
            )}
          />
          <GridColumn
            field="status"
            title={intl.formatMessage(messages.status)}
            width={'150px'}
            cell={(props) => (
              <td>
                <Wrap
                  fw={'bold'}
                  color={
                    props.dataItem.status === AchievementStatus.Confirmed
                      ? accentColorPrim
                      : orange
                  }
                >
                  {statusMap[props.dataItem.status as AchievementStatus]}
                </Wrap>
              </td>
            )}
          />
          <GridColumn
            width={'90px'}
            cell={(props) => (
              <StyledTD>
                <Button
                  icon
                  transparent
                  onClick={(e: any) => {
                    e.preventDefault();
                    removeRecord(props.dataItem.id);
                  }}
                >
                  <DeleteIcon color={red} width={'2rem'} />
                </Button>
              </StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>

      <Wrap grow={'1'} overflow={'auto'} width={'100%'} hiddenSM>
        {listAchievements.map((achievement: any, index) => (
          <Wrap
            flexDir={'column'}
            flexWrap={'wrap'}
            margin={'0 0 2rem 0'}
            width={'100%'}
            padding={'3rem'}
            br={'1.2rem'}
            bgColor={white}
            key={`card-${index}`}
          >
            <Wrap
              alignItems={'center'}
              justifyCont={'space-between'}
              width={'100%'}
              margin={'0 0 2rem 0'}
            >
              <Wrap maxWidth={'calc(100% - 5rem)'} fs={'2rem'} fw={'bold'}>
                {achievement.event.name}
              </Wrap>
              <Button
                icon
                transparent
                onClick={(e: any) => {
                  e.preventDefault();
                  removeRecord(achievement.id);
                }}
              >
                <DeleteIcon color={red} width={'2rem'} />
              </Button>
            </Wrap>
            <Wrap
              alignItems={'center'}
              width={'100%'}
              justifyCont={'space-between'}
              margin={'0 0 2rem 0'}
            >
              <Wrap
                flexDir={'column'}
                width={'100%'}
                maxWidth={'calc(50% - 1rem)'}
              >
                <Wrap
                  fs={'1.4rem'}
                  color={fontColorSecond}
                  margin={'0 1.6rem 0 0'}
                >
                  {intl.formatMessage(messages.confirmedBy)}
                </Wrap>
                <Wrap fw={'bold'}>
                  {achievement.status !== AchievementStatus.Pending
                    ? `${achievement.confirmedByPerson?.firstName} ${achievement.confirmedByPerson?.lastName}`
                    : '-'}
                </Wrap>
              </Wrap>
              <Wrap
                flexDir={'column'}
                width={'100%'}
                maxWidth={'calc(50% - 1rem)'}
              >
                <Wrap
                  fs={'1.4rem'}
                  color={fontColorSecond}
                  margin={'0 1.6rem 0 0'}
                >
                  {intl.formatMessage(messages.status)}
                </Wrap>
                <Wrap fw={'bold'} color={fontColorSecond}>
                  {statusMap[achievement.status as AchievementStatus]}
                </Wrap>
              </Wrap>
            </Wrap>
            <Wrap
              alignItems={'center'}
              width={'100%'}
              justifyCont={'space-between'}
              margin={'0 0 1rem 0'}
            >
              <Wrap
                flexDir={'column'}
                width={'100%'}
                maxWidth={'calc(50% - 1rem)'}
                padding={'0 1rem 0 0'}
                borders={[{ side: 'right', color: accentColorSecond }]}
              >
                <Wrap
                  fs={'1.4rem'}
                  color={fontColorSecond}
                  margin={'0 1.6rem 0 0'}
                >
                  {intl.formatMessage(messages.percentage)}
                </Wrap>
                <Wrap fw={'bold'}>{achievement.rankPercentage}</Wrap>
              </Wrap>
              <Wrap
                flexDir={'column'}
                width={'100%'}
                maxWidth={'calc(50% - 1rem)'}
              >
                <Wrap
                  fs={'1.4rem'}
                  color={fontColorSecond}
                  margin={'0 1.6rem 0 0'}
                >
                  {intl.formatMessage(messages.result)}
                </Wrap>
                <Wrap fw={'bold'}>
                  {achievement.event.input === WorldRecordType.Time
                    ? convertToTimeMask(achievement.levelOrResult)
                    : achievement.levelOrResult}
                </Wrap>
              </Wrap>
            </Wrap>
            <Wrap
              flexDir={'column'}
              width={'100%'}
              margin={'1rem 0'}
              maxWidth={'calc(50% - 1rem)'}
            >
              <Wrap
                fs={'1.4rem'}
                color={fontColorSecond}
                margin={'0 1.6rem 0 0'}
              >
                {intl.formatMessage(messages.startDate)}
              </Wrap>
              <Wrap fw={'bold'}>
                <Moment format={'DD MMM YYYY'}>
                  {achievement.achievedOrStart}
                </Moment>
              </Wrap>
            </Wrap>
            <Wrap alignItems={'center'}>
              <Wrap color={fontColorSecond}>
                {achievement.status !== AchievementStatus.Pending
                  ? `${achievement.confirmedByPerson?.email}`
                  : '-'}
              </Wrap>
            </Wrap>
          </Wrap>
        ))}
      </Wrap>

      {showAchievementDetails && (
        <DialogWrap
          title={
            activeAchievement?.id
              ? intl.formatMessage(messages.achievementDetails)
              : intl.formatMessage(messages.addAchievement)
          }
          children={
            <AchievementDetails
              achievement={activeAchievement}
              onCancel={() => {
                setActiveAchievement();
                setShowAchievementDetails(false);
              }}
              onSave={handleAddEditAchievement}
              onDelete={deleteAchievement}
            />
          }
        />
      )}
      {showConfirmation && confirmationHandler && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={confirmationHandler}
        />
      )}
    </Wrap>
  );
};

export default connector(Achievements);
